/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Button, Alert, FormControlLabel, Switch } from '@mui/material';
import { selectNotification } from 'modules/common/notifications/selectors';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { toast } from 'react-toastify';
import { usersActions } from 'modules/users/slice';
import { selectCurrentUser, selectLandingPage } from 'modules/users/selectors';
import { notificationActions } from 'modules/common/notifications/slice';
import OutlinedDiv from './outer-card';
import { DEMO_ROLE } from '../../../common/constants/roles';
import MODULE from '../../../common/constants/modules';
import TOAST_TYPES from '../../../common/constants/toast-types';

/**
 * Update notification implementation to update notification preferences
 * @param {string} organizationId - organization id
 * @param {function} onClose - function to execute on close
 * @returns
 */
const UpdateNotification = ({ organizationId, onClose }) => {
  const dispatch = useDispatch();
  //
  const notification = useSelector(selectNotification);
  const currentUser = useSelector(selectCurrentUser);
  const customLandingPage = useSelector(selectLandingPage);

  const [initialValues, setInitialValues] = useState({
    isAlertEmailBundled: 'true',
    isAuditEmailBundled: 'true',
  });
  // Triggers when currentUser changes
  useEffect(() => {
    // set initial values based on current user
    if (currentUser) {
      setInitialValues({
        isAlertEmailBundled: customLandingPage?.isAlertEmailBundled ?? true,
        isAuditEmailBundled: customLandingPage?.isAuditEmailBundled ?? true,
      });
    }
  }, [currentUser, customLandingPage]);

  // update user details in preference
  const onSubmitUpdateNotification = async (values) => {
    //
    dispatch(
      usersActions.updateLandingPage({
        isAlertEmailBundled: values?.isAlertEmailBundled,
        isAuditEmailBundled: values?.isAuditEmailBundled,
        landingPage: customLandingPage?.landingPage,
        ...(currentUser?.roles?.[0]?.name !== DEMO_ROLE && {
          isDemoModeActivated: customLandingPage?.isDemoModeActivated,
        }),
        organizationId,
        userId: currentUser?.id,
      })
    );
  };
  // reset notifications on first render
  useEffect(() => {
    dispatch(notificationActions.resetNotification());
  }, []);

  // Triggers when notification state changes
  useEffect(() => {
    // check notification status and toast notification
    if (
      notification?.isEnabled &&
      notification?.type === ERROR_TYPES.SUCCESS &&
      notification?.module !== MODULE.preference
    ) {
      toast(notification?.message, {
        type: TOAST_TYPES.SUCCESS,
      });
      onClose();
    }
  }, [notification]);
  //
  return (
    <Box
      xs={12}
      mt={2}
      component={Grid}
      container
      item
      display="flex"
      direction="column"
      sx={{ backgroundColor: 'white', flexShrink: 0 }}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmitUpdateNotification}
      >
        {({ handleChange, handleSubmit, isSubmitting, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid alignItems="center" px={4} my={4} xs={12} item>
              {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                <Alert mt={2} mb={3} severity={notification?.type}>
                  {notification?.message}
                </Alert>
              )}
            </Grid>
            <OutlinedDiv label="Email Configuration">
              <Grid container spacing={2} alignItems="center" px={4} my={4}>
                {/* Bundle Audit Emails */}
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isAuditEmailBundled"
                        checked={values.isAuditEmailBundled}
                        onChange={handleChange}
                        size="medium"
                      />
                    }
                    label="Bundle Alert Emails"
                    labelPlacement="end"
                    sx={{ width: 'fit-content', height: 5 }}
                  />
                </Grid>

                {/* Bundle Alert Emails */}
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isAlertEmailBundled"
                        checked={values.isAlertEmailBundled}
                        onChange={handleChange}
                        size="medium"
                      />
                    }
                    label="Bundle Alert Emails"
                    labelPlacement="end"
                    sx={{ width: 'fit-content', height: 5 }}
                  />
                </Grid>
              </Grid>
            </OutlinedDiv>

            <Grid container direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={() => onClose()}
                sx={{ marginLeft: 2, marginRight: 4 }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                Save
              </Button>
            </Grid>
          </form>
        )}
      </Formik>
    </Box>
  );
};
//
export default UpdateNotification;
